import {
  Integration,
  MerchantCashAdvance,
  Rights,
  Transaction,
  useMcaIntegrationTransactionsQuery,
  useSaveTransactionTypeMutation
} from '../../graphql/types';
import { Backdrop, Box, CircularProgress, Tooltip } from '@mui/material';
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import MainCard from '../../components/MainCard';
import { useTheme } from '@mui/material/styles';
import { AgGridReact } from 'ag-grid-react';
import useConfig from '../../hooks/useConfig';
import { GridApi, ICellRendererParams, SideBarDef } from 'ag-grid-enterprise';
import { DefaultAgGridTooltip } from '../../components/ag-grid/DefaultAgGridTooltip';
import {
  ColumnMovedEvent,
  ColumnResizedEvent,
  ColumnVisibleEvent,
  SortChangedEvent,
  GetContextMenuItemsParams,
  GridReadyEvent,
  MenuItemDef,
  FilterChangedEvent,
  ColDef,
  RowClassParams
} from 'ag-grid-community';
import { getFormattedDateUTC } from '../../utils/getFormattedDate';
import { getRowClassStyle } from '../../components/ag-grid/utility';
import accountCellRenderer from '../../components/ag-grid/AgGridAccountCellRenderer';
import { clearedTransactions, sharedCompactViewKey } from './McaTransactions';
import {
  TransactionAmountCell,
  TransactionDefaultTypeCell,
  TransactionStatusCell
} from '../../components/ag-grid/AgGridTransactionsChipCell';
import useAuth from '../../hooks/useAuth';
import McaTransactionsContextMenu from './MCATransactionsContextMenu';
import { allowedEditableTransactionTypes } from '../../pages/transactions/transactions-list';
import { Icon, supportedPaymentMethodIdsforIcons } from '../../components/icons';
import PaymentMethodsLogo from '../../utils/paymentMethodLogo';
import { underscoreToSpace } from '../../utils/strings';
import useAgGridOutsideClick from '../../components/ag-grid/useAgGridOutsideClick';
import { LinkOutlined } from '@ant-design/icons';
import { applyGridColumnState } from '../../utils/agGridUtils';

interface Props {
  mca: MerchantCashAdvance;
  integration: Integration;
}
export const mcaRecurrencyKeyName = 'MCAS_RECURRENCY_GRID';
const McaRecurrency = ({ mca, integration }: Props) => {
  const theme = useTheme();
  const { mode } = useConfig();
  const gridStyle = useMemo(() => ({ height: '100%', width: '100%' }), []);
  const { user: currentUser } = useAuth();
  const userId = currentUser?.id;
  const keyName = `${userId}_${mcaRecurrencyKeyName}`;
  const compactViewKey = `${userId}_${sharedCompactViewKey}`;
  const useCompactView = localStorage.getItem(compactViewKey + '_USE_COMPACT_VIEW') === 'false' ? null : true;
  const isSuperAdmin = currentUser?.rights.includes(Rights.IsSuperAdmin);
  const [updateTransactionType] = useSaveTransactionTypeMutation();
  const { data } = useMcaIntegrationTransactionsQuery({
    variables: { mcaId: mca.id, integrationId: integration.id },
    fetchPolicy: 'network-only'
  });
  const [gridApi, setGridApi] = useState<GridApi | null>(null);

  const defaultColDef = useMemo(() => {
    return {
      editable: false,
      sortable: true,
      resizable: true,
      pivot: true,
      filter: false,
      cellStyle: {
        display: 'flex',
        alignItems: 'center'
      },
      suppressHeaderMenuButton: true,
      columnChooserParams: {
        suppressColumnFilter: true,
        contractColumnSelection: true
      },
      tooltipComponent: DefaultAgGridTooltip
    };
  }, []);

  const sideBar = useMemo<SideBarDef | string | string[] | boolean | null>(() => {
    return {
      position: 'right',
      toolPanels: [
        {
          id: 'columns',
          labelDefault: 'Columns',
          labelKey: 'columns',
          iconKey: 'columns',
          toolPanel: 'agColumnsToolPanel',
          toolPanelParams: {
            suppressRowGroups: true,
            suppressValues: true,
            suppressPivots: true,
            suppressPivotMode: true,
            suppressColumnFilter: true,
            suppressColumnSelectAll: true,
            suppressColumnExpandAll: true
          }
        }
      ]
    };
  }, []);
  const columnDefs = useMemo<ColDef[]>(
    () => [
      {
        suppressHeaderMenuButton: true,
        headerName: '#',
        field: 'number',
        cellClass: 'ag-centered-cell',
        headerClass: 'ag-header-transaction-cell-center',
        toolPanelClass: ['ag-header-transaction-cell-center']
      },
      {
        headerName: 'Name',
        field: 'name',
        tooltipField: 'name',
        headerClass: 'ag-header-cell-merchant-group',
        toolPanelClass: ['ag-header-cell-merchant-group']
      },
      {
        initialHide: true,
        headerName: 'Origination Date',
        headerClass: 'ag-header-performance-group-right',
        toolPanelClass: ['ag-header-performance-group-right'],
        field: 'originationDate',
        type: 'rightAligned',
        valueGetter: (params) => getFormattedDateUTC(params.data?.originationDate)
      },
      {
        headerName: 'Effective Date',
        headerClass: 'ag-header-performance-group-right',
        toolPanelClass: ['ag-header-performance-group-right'],
        suppressHeaderMenuButton: true,
        sort: 'desc',
        field: 'effectiveDate',
        type: 'rightAligned',
        valueGetter: (params) => getFormattedDateUTC(params.data?.effectiveDate),
        comparator: (valueA, valueB, nodeA, nodeB, isDescending) => {
          let dateA = new Date(valueA).getTime();
          let dateB = new Date(valueB).getTime();
          if (dateA === dateB) return 0;
          return dateA > dateB ? 1 : -1;
        }
      },
      {
        headerName: 'Type',
        field: 'type',
        filter: true,
        headerClass: 'ag-header-performance-group',
        toolPanelClass: ['ag-header-performance-group'],
        cellRenderer: TransactionDefaultTypeCell,
        cellRendererParams: { useCompactViewStyle: useCompactView ? true : false }
      },
      {
        headerName: 'Pmt Method',
        field: 'paymentMethod.id',
        sortable: false,
        cellClass: 'ag-centered-cell',
        headerClass: 'text-center-lightskyblue',
        toolPanelClass: ['ag-header-performance-group'],
        tooltipValueGetter: (params) => {
          const id = params.data?.paymentMethod?.id;
          return id ? underscoreToSpace(id) : 'UNKNOWN';
        },
        cellRenderer: ({ data, node }: ICellRendererParams) => {
          if (node.isRowPinned()) {
            return null;
          }
          if (supportedPaymentMethodIdsforIcons.includes(data?.paymentMethod?.id)) {
            return (
              <Tooltip content={data?.paymentMethod?.id}>
                <span className="ag-centered-cell">
                  <Icon id={data?.paymentMethod?.id} size="xxs" />
                </span>
              </Tooltip>
            );
          } else {
            return <PaymentMethodsLogo paymentMethod={data?.paymentMethod} size={'xs'} mt={0} />;
          }
        }
      },
      {
        headerName: 'Account',
        headerClass: 'ag-header-performance-group',
        toolPanelClass: ['ag-header-performance-group'],
        field: 'account',
        tooltipValueGetter: (params) => accountCellRenderer(params),
        cellClass: 'textFormat',
        cellRenderer: accountCellRenderer
      },
      {
        headerName: 'Status',
        headerClass: 'ag-header-performance-group-center',
        toolPanelClass: ['ag-header-performance-group-center'],
        field: 'status',
        filter: true,
        cellRendererParams: { useCompactViewStyle: useCompactView ? true : false },
        cellRenderer: TransactionStatusCell,
        cellClass: 'ag-centered-cell' 
      },
      {
        headerName: 'Amount',
        field: 'amount',
        cellRenderer: TransactionAmountCell,
        cellRendererParams: { useCompactViewStyle: useCompactView ? true : false },
        type: 'rightAligned',
        headerClass: 'ag-header-performance-group-center',
        toolPanelClass: ['ag-header-performance-group-center'],
        cellClass: 'ag-centered-cell'
      },
      {
        headerName: 'Deposit Date',
        headerClass: 'ag-header-performance-group-right',
        toolPanelClass: ['ag-header-performance-group-right'],
        field: 'originationDate',
        type: 'rightAligned',
        valueGetter: (params) => getFormattedDateUTC(params.data?.originationDate)
      },
      {
        initialHide: true,
        headerName: 'Notes',
        field: 'notes',
        headerClass: 'ag-header-performance-group',
        toolPanelClass: ['ag-header-performance-group'],
        tooltipField: 'notes'
      },
      {
        initialHide: true,
        headerName: 'To',
        field: 'to',
        headerClass: 'ag-header-performance-group',
        toolPanelClass: ['ag-header-performance-group'],
        tooltipField: 'to'
      },
      {
        initialHide: true,
        headerName: 'Return Date',
        headerClass: 'lightsalmon',
        toolPanelClass: ['ag-header-fees-group-right'],
        field: 'returnDate',
        type: 'rightAligned',
        valueGetter: (params) => getFormattedDateUTC(params.data?.returnDate)
      },
      {
        headerName: 'Return Code',
        headerClass: 'lightsalmon',
        field: 'returnCode',
        toolPanelClass: ['ag-header-fees-group-right']
      },
      {
        initialHide: true,
        headerName: 'Recurrence Description',
        field: 'recurrenceDescription',
        headerClass: 'ag-header-transaction-cell',
        toolPanelClass: ['ag-header-transaction-cell'],
        tooltipField: 'recurrenceDescription'
      },
      {
        initialHide: true,
        headerName: 'Transaction ID',
        field: 'externalId',
        headerComponent: () => {
          return (
            <Tooltip
              title="Transaction ID"
              PopperProps={{
                sx: {
                  '& .MuiTooltip-tooltip': {
                    bgcolor: '#000000',
                    color: '#FFFFFF'
                  }
                }
              }}
            >
              <LinkOutlined style={{ fontSize: 20 }} />
            </Tooltip>
          );
        },
        headerClass: 'ag-header-transaction-cell-center',
        toolPanelClass: ['ag-header-transaction-cell-center'],
        tooltipField: 'externalId'
      }
    ],
    [useCompactView]
  );

  const [backdropOpen, setBackdropOpen] = useState<boolean>(false);

  useLayoutEffect(() => {
    function updateSize() {
      if (gridApi) {
      }
    }

    window.addEventListener('resize', updateSize);
    updateSize();
    return () => window.removeEventListener('resize', updateSize);
  }, [gridApi]);

  useEffect(() => {
    if (!gridApi) {
      return;
    }
    gridApi.setGridOption('pinnedBottomRowData', [
      {
        amount: data?.mcaIntegrationTransactions.reduce((accumulator, transaction) => {
          return accumulator + (clearedTransactions.includes(transaction.status) ? transaction.amount || 0 : 0);
        }, 0)
      }
    ]);
  }, [data, gridApi]);

  const onGridReady = useCallback((params: GridReadyEvent) => {
    setGridApi(params.api);
    applyGridColumnState(params.api, keyName);
  }, []);

  const onRowDataUpdated = useCallback(() => {
    if (!gridApi) return;
    applyGridColumnState(gridApi, keyName);
  }, [gridApi]);
  useAgGridOutsideClick(gridApi);
  type excelTypes = {
    id: string;
    dataType: any;
  };

  const excelStyles: excelTypes[] = [
    {
      id: 'textFormat',
      dataType: 'String'
    }
  ];

  const onFilterChanged = useCallback(({ api }: FilterChangedEvent) => {
    let amountSum = 0;
    api.forEachNodeAfterFilter((rowNode) => {
      if (rowNode.data && clearedTransactions.includes(rowNode.data.status)) {
        amountSum += rowNode.data.amount;
      }
    });
    api.setGridOption('pinnedBottomRowData', [{ amount: amountSum }]);
  }, []);

  const pinnedBottomRowData = useMemo<any[]>(() => {
    return [{ amount: 0 }];
  }, []);

  const getParams = () => ({
    processCellCallback(params: any) {
      const value = params.value;
      return value === undefined || params.node.rowPinned === 'bottom' ? '' : `${value}`;
    }
  });

  const getCsvParams = () => ({
    processCellCallback(params: any) {
      const value = params.value;
      return value === undefined || params.node.rowPinned === 'bottom' ? '' : `${value}`;
    }
  });

  const defaultExcelExportParams = useMemo(() => {
    return getParams();
  }, []);

  const defaultCsvExportParams = useMemo(() => {
    return getCsvParams();
  }, []);

  const getRowClass = (params: RowClassParams): string | string[] => {
    return getRowClassStyle(params, mode);
  };
  const getContextMenuItems = useCallback(
    (params: GetContextMenuItemsParams): (MenuItemDef | string)[] => {
      return McaTransactionsContextMenu({
        params,
        updateTransactionType,
        mcaId: mca.id,
        isSuperAdmin,
        allowedEditableTransactionTypes
      });
    },
    [isSuperAdmin, mca.id, updateTransactionType]
  );
  const saveColumnsStateOnChange = useCallback(
    (event: ColumnResizedEvent | SortChangedEvent | ColumnMovedEvent | ColumnVisibleEvent) => {
      if (
        gridApi && event.source?.toLowerCase().includes('ui') &&
        (event.type === 'columnResized' && event.finished) ||
        (event.type === 'columnMoved' && event.finished) ||
        event.type === 'sortChanged' ||
        event.type === 'columnVisible'
      ) {

        if (event.type === 'columnVisible') {
          const colId = event.column?.getColId();
          if (colId && event.column?.isVisible()) {
            gridApi?.autoSizeColumns([colId], true);
          }
        }
        const columnsState = JSON.stringify(gridApi?.getColumnState());
        localStorage.setItem(keyName + '_COLUMNS_STATE', columnsState);
      }
    },
    [gridApi]
  );
  return (
    <Box display="flex" flexDirection="column">
      <MainCard
        border={false}
        boxShadow
        shadow={theme.customShadows.z1}
        sx={{ height: '100%', minHeight: '500px', p: 2, ml: -0.5, mr: 0.5 }}
        content={false}
      >
        <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={backdropOpen}>
          <CircularProgress color="inherit" />
        </Backdrop>
        {/*  <Stack direction={'row'} spacing={1} pb={4}>
          <RecurrencyTitle recurrency={recurrency} />
        </Stack>*/}
        <div
          className={`${mode === 'light' ? 'ag-theme-material' : 'ag-theme-material-dark'} ${
            useCompactView ? 'compact-view' : 'expanded-view'
          }`}
          style={gridStyle}
        >
          <AgGridReact<Transaction[]>
            suppressColumnVirtualisation={true}
            onRowDataUpdated={onRowDataUpdated}
            domLayout={'autoHeight'}
            sideBar={sideBar}
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            rowData={data?.mcaIntegrationTransactions || []}
            onGridReady={onGridReady}
            onFilterChanged={onFilterChanged}
            pinnedBottomRowData={pinnedBottomRowData}
            onColumnResized={saveColumnsStateOnChange}
            onSortChanged={saveColumnsStateOnChange}
            onColumnMoved={saveColumnsStateOnChange}
            onColumnVisible={saveColumnsStateOnChange}
            getRowClass={getRowClass}
            defaultExcelExportParams={defaultExcelExportParams}
            defaultCsvExportParams={defaultCsvExportParams}
            excelStyles={excelStyles}
            getContextMenuItems={getContextMenuItems}
          ></AgGridReact>
        </div>
      </MainCard>
    </Box>
  );
};

export default McaRecurrency;
